import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAffiliateById } from '../../services/companyAuthService';


const ViewAffiliate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAffiliate();
  }, [id]);

  const groupCampaigns = (campaigns) => {
    const groupedMap = new Map();
    
    campaigns.forEach(campaignItem => {
      const campaignId = campaignItem.campaign._id;
      if (!groupedMap.has(campaignId)) {
        groupedMap.set(campaignId, {
          campaign: campaignItem.campaign,
          status: campaignItem.status,
          _id: campaignItem._id
        });
      }
    });

    return Array.from(groupedMap.values());
  };

  const fetchAffiliate = async () => {
    try {
      setLoading(true);
      const data = await getAffiliateById(id);
      if (data.campaigns) {
        data.campaigns = groupCampaigns(data.campaigns);
        data.campaignCount = data.campaigns.length;
      }
      setAffiliate(data);
    } catch (error) {
      console.error('Error fetching affiliate:', error);
      toast.error(error.message || 'Failed to fetch affiliate details');
    } finally {
      setLoading(false);
    }
  };

  // const handleViewCampaigns = () => {
  //   navigate(`/company/dashboard/affiliates/affiliate-campaigns/${encodeURIComponent(email)}`);
  // };
  const handleViewCampaigns = () => {
    if (affiliate?.email) {
      navigate(`/company/dashboard/affiliates/affiliate-campaigns/${encodeURIComponent(affiliate.email)}`);
    } else {
      toast.error("Email not available.");
    }
  };

  if (loading) {
    return (
      <>

        <div className="flex justify-center items-center min-h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>

      </>
    );
  }

  if (!affiliate) {
    return (
      <>

        <div className="flex justify-center items-center min-h-full">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800">Affiliate not found</h2>
            <button
              onClick={() => navigate('/company/dashboard/affiliates/')}
              className="mt-4 bg-orange-500 text-white px-2 py-2 rounded-lg hover:bg-orange-600"
            >
              Back
            </button>
          </div>
        </div>

      </>
    );
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row min-h-full">
        <div className="flex-1 p-2 md:p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white shadow-lg rounded-xl p-6">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Affiliate Details</h2>
                <button
                  onClick={() => navigate('/company/dashboard/affiliates/')}
                  className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-orange-200 transition-colors duration-200"
                >
                  Back
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profile Information */}
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Profile Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-500">Name</label>
                      <p className="font-medium text-gray-900">
                        {affiliate.firstName && affiliate.lastName 
                          ? `${affiliate.firstName} ${affiliate.lastName}`
                          : affiliate.username || 'Unnamed Affiliate'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <p className="font-medium text-gray-900">{affiliate.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Type</label>
                      <p className="font-medium text-gray-900 capitalize">{affiliate.affiliateType}</p>
                    </div>
                    {affiliate.companyName && (
                      <div>
                        <label className="text-sm text-gray-500">Company Name</label>
                        <p className="font-medium text-gray-900">{affiliate.companyName}</p>
                      </div>
                    )}
                    {affiliate.instagramId && (
                      <div>
                        <label className="text-sm text-gray-500">Instagram ID</label>
                        <p className="font-medium text-gray-900">{affiliate.instagramId}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Payment Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-500">Payment Method</label>
                      <p className="font-medium text-gray-900 capitalize">{affiliate.paymentInfo?.method}</p>
                    </div>
                    {affiliate.paymentInfo?.method === 'bank' && (
                      <>
                        <div>
                          <label className="text-sm text-gray-500">Bank Name</label>
                          <p className="font-medium text-gray-900">{affiliate.paymentInfo?.bankName}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Account Number</label>
                          <p className="font-medium text-gray-900">{affiliate.paymentInfo?.accountNumber}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">IFSC Code</label>
                          <p className="font-medium text-gray-900">{affiliate.paymentInfo?.ifscCode}</p>
                        </div>
                      </>
                    )}
                    {affiliate.paymentInfo?.method === 'upi' && (
                      <div>
                        <label className="text-sm text-gray-500">UPI ID</label>
                        <p className="font-medium text-gray-900">{affiliate.paymentInfo?.upiId}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Associated Campaigns */}
                {affiliate.campaigns && affiliate.campaigns.length > 0 && (
                  <div className="md:col-span-2 bg-gray-50 p-6 rounded-xl mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Associated Campaigns ({affiliate.campaignCount || affiliate.campaigns.length})
                      </h3>
                      <button
                        onClick={handleViewCampaigns}
                        className="text-orange-500 hover:text-orange-600 text-sm font-medium"
                      >
                        View All Campaigns
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {affiliate.campaigns.map((campaignItem) => (
                        <div key={campaignItem._id} className="bg-white p-4 rounded-lg shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{campaignItem.campaign.name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              campaignItem.status === 'accepted'
                                ? 'bg-green-100 text-green-800'
                                : campaignItem.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : campaignItem.status === 'rejected'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}>
                              {campaignItem.status}
                            </span>
                          </div>
                          {campaignItem.campaign.description && (
                            <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                              {campaignItem.campaign.description}
                            </p>
                          )}
                          {campaignItem.campaign.commission && (
                            <p className="text-sm font-medium text-gray-900">
                              Commission: {
                                campaignItem.campaign.commission.model === 'fixed'
                                  ? `${campaignItem.campaign.commission.amount} ${campaignItem.campaign.commission.currency?.symbol}`
                                  : `${campaignItem.campaign.commission.percentage}%`
                              }
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewAffiliate; 